import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Nav from "./Nav";
import Footer from "./Footer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faRocket, 
  faMagic, 
  faChartLine, 
  faWallet, 
  faStar,
  faFire
} from "@fortawesome/free-solid-svg-icons";

function Creator() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const creatorPerks = []; // Cleaned up for new Bento UI

  return (
    <div className="bg-white min-h-screen">
      <Nav />
      
      {/* Creator Hero - Aligned with Home UI */}
      <section className="pt-48 pb-32 px-4 relative overflow-hidden flex flex-col items-center justify-center text-center">
        {/* Vibrant Background Gradients */}
        <div className="absolute top-0 right-1/4 w-[700px] h-[700px] bg-blue-100/30 blur-[130px] rounded-full -z-10 animate-pulse"></div>
        <div className="absolute bottom-0 left-1/4 w-[700px] h-[700px] bg-purple-100/30 blur-[130px] rounded-full -z-10"></div>

        <div className="z-20 max-w-5xl">
          <div className="inline-flex items-center gap-2 px-6 py-2 bg-black text-white rounded-full text-xs font-bold uppercase tracking-widest mb-12 shadow-2xl">
             <FontAwesomeIcon icon={faFire} className="text-orange-400 animate-pulse" /> For the Visionaries
          </div>
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-black mb-10 leading-[1.05] tracking-tight">
            Turn Your Viral <br className="hidden lg:block" />
            <span className="inline-block px-12 py-1.5 bg-transparent text-blue-600 border-[3px] border-blue-600 rounded-full mx-2 transform -rotate-1 hover:rotate-0 transition-transform cursor-default">
              Creativity
            </span> <br className="hidden lg:block" />
            into Currency <br className="hidden lg:block" />
            and Growth
          </h1>
          
          <p className="mb-14 text-base md:text-lg lg:text-xl text-gray-500 max-w-2xl mx-auto font-medium px-4 opacity-80 leading-relaxed">
            Braccet is where the next generation of viral content meets the world's most innovative brands.
          </p>

          <Link 
            to="/onboarding/creator"
            className="inline-block px-14 py-6 bg-black text-white rounded-full text-xl font-bold shadow-2xl transition-all hover:scale-105 active:scale-95 hover:shadow-black/20"
          >
            Start Creating Now
          </Link>
        </div>
      </section>

      <section className="py-40 px-6 bg-[#f8fafc] relative overflow-hidden">
        {/* Decorative Background Accents */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100/20 blur-[130px] rounded-full -z-10 translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-100/20 blur-[130px] rounded-full -z-10 -translate-x-1/2 translate-y-1/2"></div>

        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-24 max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-black text-gray-900 mb-8 tracking-tighter leading-none">
              Why Creators <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 italic">Love</span> Braccet
            </h2>
            <p className="text-xl text-gray-500 font-medium opacity-80">
              The tools you need to build a sustainable career in creativity.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 lg:grid-cols-12 gap-6 auto-rows-[250px] md:auto-rows-[300px]">
            {/* Box 1: Monetize Content (Yellow) - Tall Card */}
            <div className="md:col-span-3 lg:col-span-7 row-span-2 group relative bg-[#FFD54F] rounded-[3rem] md:rounded-[3.5rem] p-8 md:p-12 overflow-hidden flex flex-col md:justify-end transition-all duration-500 hover:scale-[1.01] hover:shadow-2xl min-h-[450px] md:min-h-0">
              <div className="md:absolute top-12 left-12 space-y-4 w-full md:w-1/2 z-10 mb-12 md:mb-0">
                 <div className="bg-white/90 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4 shadow-lg border border-white/50 flex items-center gap-3 md:gap-4 w-fit animate-float-slow">
                   <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white text-[10px] shadow-sm"><FontAwesomeIcon icon={faFire} /></div>
                   <div className="font-black text-gray-800 text-sm md:text-base">High Earnings</div>
                 </div>
                 <div className="bg-white/80 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4 shadow-lg border border-white/50 flex items-center gap-3 md:gap-4 ml-8 md:ml-12 animate-float-mid">
                   <div className="w-8 h-8 rounded-full bg-yellow-500 flex items-center justify-center text-white text-[10px]"><FontAwesomeIcon icon={faStar} /></div>
                   <div className="font-black text-gray-800 text-sm md:text-base">Medium Deals</div>
                 </div>
                 <div className="bg-white/70 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4 shadow-lg border border-white/50 flex items-center gap-3 md:gap-4 ml-4 md:ml-6 animate-float-fast">
                   <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-[10px]"><FontAwesomeIcon icon={faWallet} /></div>
                   <div className="font-black text-gray-800 text-sm md:text-base">Consistent Payouts</div>
                 </div>
              </div>

              <div className="relative z-10 w-full md:w-2/3 md:ml-auto">
                <h3 className="text-3xl md:text-4xl font-black text-gray-950 mb-4 md:mb-6 leading-[1.1] tracking-tighter">
                  Auto-prioritize <br /> your earnings.
                </h3>
                <p className="text-gray-900/70 text-base md:text-lg font-bold leading-relaxed max-w-sm">
                  Smart algorithms that decide which brand deals to take first for maximum impact and growth.
                </p>
              </div>
            </div>

            {/* Box 2: Viral Reach (Blue) - Regular Card */}
            <div className="md:col-span-2 lg:col-span-5 row-span-2 bg-[#5C8DFF] rounded-[3.5rem] p-12 overflow-hidden flex flex-col justify-between transition-all duration-500 hover:scale-[1.01] hover:shadow-2xl">
               <div className="relative h-1/2 flex items-center justify-center">
                  <div className="absolute w-48 h-48 bg-white/20 rounded-full blur-3xl animate-pulse"></div>
                  <div className="grid grid-cols-2 gap-4 relative z-10 animate-float-mid">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-xl text-blue-600 text-2xl rotate-12 transition-transform group-hover:rotate-0"><FontAwesomeIcon icon={faMagic} /></div>
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-xl text-purple-600 text-2xl -rotate-12 translate-y-4"><FontAwesomeIcon icon={faRocket} /></div>
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-xl text-pink-600 text-2xl -rotate-6 ml-4"><FontAwesomeIcon icon={faFire} /></div>
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-xl text-emerald-600 text-2xl rotate-6 -translate-y-2"><FontAwesomeIcon icon={faStar} /></div>
                  </div>
               </div>
               <div>
                  <h3 className="text-3xl font-black text-white mb-6 leading-tight tracking-tight">
                    Arrange into <br /> viral lists.
                  </h3>
                  <p className="text-white/80 font-bold leading-relaxed">
                    Braccet sorts your opportunities into lists that fit your content style and schedule perfectly.
                  </p>
               </div>
            </div>

            {/* Box 3: Premium Tools (Purple) - Tall Card */}
            <div className="md:col-span-2 lg:col-span-5 row-span-2 bg-[#9C6ADE] rounded-[3.5rem] p-12 overflow-hidden flex flex-col transition-all duration-500 hover:scale-[1.01] hover:shadow-2xl">
               <h3 className="text-3xl font-black text-white mb-6 leading-tight tracking-tight">
                  Group into <br /> categories.
               </h3>
               <p className="text-white/80 font-bold leading-relaxed mb-12">
                  Organize your campaigns into appropriate categories. Keep your brand separate from your lifestyle content.
               </p>
               <div className="relative flex-1">
                  <div className="absolute top-0 left-0 w-full aspect-square bg-white/10 rounded-[3rem] p-6 shadow-2xl backdrop-blur-md border border-white/20 group-hover:translate-x-4 transition-transform duration-500">
                     <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-purple-600 mb-4 shadow-lg"><FontAwesomeIcon icon={faRocket} /></div>
                     <div className="font-black text-white">Brand Collabs</div>
                  </div>
                  <div className="absolute top-20 left-12 w-full aspect-square bg-white/20 rounded-[3rem] p-6 shadow-2xl backdrop-blur-md border border-white/20 group-hover:translate-x-8 transition-transform duration-700">
                     <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-pink-600 mb-4 shadow-lg"><FontAwesomeIcon icon={faMagic} /></div>
                     <div className="font-black text-white">Gifts & Events</div>
                  </div>
               </div>
            </div>

            {/* Box 4: Career Growth (Red/Orange) - Wide Card */}
            <div className="md:col-span-3 lg:col-span-7 row-span-2 bg-[#FF8A65] rounded-[3.5rem] p-12 overflow-hidden flex flex-col justify-end transition-all duration-500 hover:scale-[1.01] hover:shadow-2xl relative">
               <div className="absolute top-12 left-1/2 -translate-x-1/2 flex items-center gap-8 z-10">
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center text-orange-600 text-3xl shadow-xl hover:scale-110 transition-transform cursor-pointer"><FontAwesomeIcon icon={faMagic} /></div>
                  <div className="w-12 h-1 bg-white/40 rounded-full"></div>
                  <div className="w-24 h-24 bg-[#FF7043] rounded-full flex items-center justify-center text-white text-4xl shadow-2xl border-4 border-white animate-pulse"><FontAwesomeIcon icon={faChartLine} /></div>
               </div>

               <div className="pt-24">
                 <h3 className="text-4xl font-black text-white mb-6 leading-tight tracking-tighter">
                    Like growth tools, <br /> but better.
                 </h3>
                 <p className="text-white/80 font-bold leading-relaxed max-w-xl">
                    Our platform makes it easier to capture every opportunity that comes your way. Just focus on your craft.
                 </p>
               </div>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes float-slow {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-15px); }
          }
          @keyframes float-mid {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          @keyframes float-fast {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-5px); }
          }
          .animate-float-slow { animation: float-slow 5s ease-in-out infinite; }
          .animate-float-mid { animation: float-mid 4s ease-in-out infinite; }
          .animate-float-fast { animation: float-fast 3s ease-in-out infinite; }
        `}</style>
      </section>

      <section className="py-24 md:py-48 px-4 md:px-6 relative overflow-hidden bg-white">
        {/* Subtle Background Accents */}
        <div className="absolute top-0 right-0 w-[400px] md:w-[800px] h-[400px] md:h-[800px] bg-blue-50/40 blur-[100px] md:blur-[150px] rounded-full translate-x-1/4 -translate-y-1/4 -z-10"></div>
        <div className="absolute bottom-0 left-0 w-[400px] md:w-[800px] h-[400px] md:h-[800px] bg-purple-50/40 blur-[100px] md:blur-[150px] rounded-full -translate-x-1/4 translate-y-1/4 -z-10"></div>

        <div className="container mx-auto max-w-7xl flex flex-col lg:flex-row items-center gap-16 lg:gap-24 relative z-10">
          <div className="lg:w-1/2 w-full">
            <h2 className="text-4xl md:text-7xl lg:text-8xl font-black mb-10 md:mb-16 leading-[0.95] tracking-tighter text-gray-950">
              The Engine For <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-pink-500 italic drop-shadow-sm">Full-Time</span> <br />
              Creativity.
            </h2>
            
            <div className="space-y-10 md:space-y-12">
              {[
                { title: "Smart Ecosystem", text: "We don't just find deals; we build your entire business infrastructure.", color: "bg-blue-600", shadow: "shadow-blue-200" },
                { title: "Instant Settlements", text: "No more waiting 90 days. Get paid as soon as the work is done.", color: "bg-purple-600", shadow: "shadow-purple-200" },
                { title: "Creator First", text: "Built by creators, for creators. We know exactly what you need to scale.", color: "bg-emerald-500", shadow: "shadow-emerald-200" }
              ].map((item, i) => (
                <div key={i} className="flex gap-6 md:gap-8 items-start group cursor-default">
                  <div className={`flex-shrink-0 w-12 h-12 md:w-16 md:h-16 rounded-[1.2rem] md:rounded-[1.5rem] ${item.color} flex items-center justify-center font-black text-white text-xl md:text-2xl shadow-xl ${item.shadow} transition-all duration-500 group-hover:scale-110 group-hover:rotate-3`}>
                    0{i + 1}
                  </div>
                  <div className="pt-1 md:pt-2">
                    <h4 className="text-xl md:text-3xl font-black mb-2 md:mb-3 tracking-tight text-gray-900 group-hover:text-gray-950 transition-colors uppercase text-sm tracking-[0.2em]">{item.title}</h4>
                    <p className="text-gray-500 text-base md:text-lg font-medium leading-relaxed opacity-80 group-hover:opacity-100 transition-opacity">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="lg:w-1/2 w-full mt-12 lg:mt-0">
            <div className="relative group">
              {/* Premium Dashboard Container */}
              <div className="bg-white/80 border border-gray-100/50 rounded-[2.5rem] md:rounded-[4.5rem] p-6 md:p-14 shadow-[0_50px_100px_rgba(0,0,0,0.08)] backdrop-blur-3xl overflow-hidden relative">
                <div className="flex justify-between items-center mb-10 md:mb-16 px-2">
                   <div>
                      <div className="text-[9px] md:text-[10px] font-black tracking-[0.3em] uppercase text-blue-600 mb-2">Network Pulse</div>
                      <div className="text-2xl md:text-3xl font-black tracking-tighter text-gray-950">Real-time Activity</div>
                   </div>
                  <div className="flex flex-col items-end">
                    <div className="px-3 md:px-4 py-1.5 md:py-2 bg-green-50 text-green-600 border border-green-100 rounded-full text-[9px] md:text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 md:gap-2 shadow-sm transition-all group-hover:scale-105">
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full animate-ping"></div>
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full absolute"></div>
                      System Live
                    </div>
                  </div>
                </div>

                <div className="space-y-3 md:space-y-4">
                  {[
                    { user: "Sarah K.", action: "Just secured", amount: "$4,500", brand: "Nike", time: "2m ago", color: "bg-blue-500" },
                    { user: "Marcus V.", action: "Earned", amount: "$1,200", brand: "Sony", time: "12m ago", color: "bg-purple-500" },
                    { user: "Elena R.", action: "Payout", amount: "$8,900", brand: "Adobe", time: "24m ago", color: "bg-pink-500" },
                    { user: "James T.", action: "New Deal", amount: "$2,800", brand: "Razer", time: "38m ago", color: "bg-emerald-500" }
                  ].map((drop, i) => (
                    <div key={i} className="flex items-center justify-between p-4 md:p-7 bg-gray-50/40 rounded-[2rem] md:rounded-[2.5rem] border border-gray-100/50 hover:bg-white hover:shadow-xl hover:shadow-gray-200/50 hover:-translate-y-1 transition-all duration-300 group/item cursor-default">
                      <div className="flex items-center gap-3 md:gap-5">
                        <div className={`w-10 h-10 md:w-14 md:h-14 ${drop.color} rounded-xl md:rounded-2xl flex items-center justify-center text-white font-black text-lg md:text-xl shadow-lg transform group-hover/item:scale-110 group-hover/item:rotate-3 transition-all`}>
                          {drop.user[0]}
                        </div>
                        <div>
                          <div className="font-black text-base md:text-xl text-gray-950 mb-0.5 tracking-tight">{drop.user}</div>
                          <div className="text-[9px] md:text-[10px] text-gray-400 font-black uppercase tracking-[0.15em]">{drop.action} • {drop.brand}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg md:text-2xl font-black text-blue-600 tracking-tighter mb-0.5">{drop.amount}</div>
                        <div className="text-[9px] md:text-[10px] text-gray-400 font-bold uppercase tracking-widest">{drop.time}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Decorative Bottom Glow */}
                <div className="absolute -bottom-20 left-0 w-full h-40 bg-gradient-to-t from-blue-50/50 to-transparent pointer-events-none"></div>
              </div>

              {/* Impact Badge - Enhanced Overlap & Glow */}
              <div className="absolute -bottom-6 -right-2 md:-bottom-10 md:-right-6 bg-gray-950 p-6 md:p-10 rounded-[2.5rem] md:rounded-[3.5rem] shadow-[0_30px_60px_rgba(0,0,0,0.3)] border-[4px] md:border-[6px] border-white animate-float-mid z-30 scale-75 md:scale-100 transform origin-bottom-right">
                <div className="relative">
                   <div className="absolute -inset-4 bg-blue-500/20 blur-2xl rounded-full -z-10 animate-pulse"></div>
                   <div className="text-3xl md:text-5xl font-black leading-none text-white tracking-tighter">$2.4M+</div>
                   <div className="text-[10px] md:text-[11px] font-black uppercase tracking-[0.2em] mt-2 md:mt-3 text-white/50">Paid This Month</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA - Supercharged & Light Theme */}
      <section className="py-48 px-4 relative overflow-hidden bg-white">
        {/* Deep Glowing Backgrounds (Subtle for Light Mode) */}
        <div className="absolute top-1/2 left-1/4 w-[800px] h-[800px] bg-blue-50 blur-[150px] rounded-full -translate-y-1/2"></div>
        <div className="absolute top-1/2 right-1/4 w-[800px] h-[800px] bg-purple-50 blur-[150px] rounded-full -translate-y-1/2"></div>
        
        <div className="container mx-auto max-w-6xl text-center relative z-10">
          <div className="inline-flex items-center gap-3 px-6 py-2 bg-gray-50 border border-gray-100 text-gray-500 rounded-full text-sm font-bold uppercase tracking-[0.2em] mb-12 shadow-sm">
            <FontAwesomeIcon icon={faStar} className="text-orange-400" /> Limited Alpha Access
          </div>

          <h2 className="text-6xl md:text-9xl font-black text-gray-900 mb-14 leading-[0.85] tracking-tighter">
            YOUR CONTENT <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600">DESERVES CASH.</span>
          </h2>
          
          <p className="text-xl md:text-2xl text-gray-400 mb-20 font-medium max-w-2xl mx-auto opacity-80">
            Join the 1% of creators who treat their creativity like a high-growth tech startup.
          </p>
          
          <Link 
            to="/onboarding/creator"
            className="group relative inline-flex items-center gap-6 px-16 py-8 bg-black text-white rounded-full text-2xl font-black shadow-[0_20px_50px_rgba(0,0,0,0.2)] transition-all hover:scale-110 active:scale-95 hover:bg-gray-900"
          >
             SECURE MY SEAT
             <div className="w-10 h-10 bg-white text-black rounded-full flex items-center justify-center group-hover:rotate-12 transition-transform">
               <FontAwesomeIcon icon={faRocket} className="text-sm" />
             </div>
          </Link>
        </div>

        {/* Floating Decorative Icons */}
        <div className="absolute top-20 left-20 text-gray-100 text-9xl -rotate-12"><FontAwesomeIcon icon={faMagic} /></div>
        <div className="absolute bottom-20 right-20 text-gray-100 text-9xl rotate-12"><FontAwesomeIcon icon={faWallet} /></div>
      </section>

      <Footer />
    </div>
  );
}

export default Creator;
